import { Badge, Button, Col, Row } from "react-bootstrap";
import { useLoaderData, useNavigate, useRevalidator, useRouteLoaderData } from "react-router-dom";
import { Invoice, OrderAdjustReq, OrderInfo, ParkerDetails } from "./Data/ApiTypes";
import { cents, showMoney, toCents, Money } from "./Data/Money";
import { showDate, showInstantDate, showInstantDateTime } from "./Data/Date";
import { showAddressHtml } from "./Data/Location";
import { Envelope, InfoCircle, Printer, Telephone } from "react-bootstrap-icons";
import { PlusSlashMinus } from "react-bootstrap-icons";
import { showOrderType } from "./Data/Finance";
import { LargePage } from "./Controls/LargePage";
import { equal, add, subtract } from "dinero.js";
import { useParkerBalance } from "./hooks/useParkerBalance";
import React, { useState } from "react";
import { useAtom } from "jotai";
import { authAtom } from "./Data/Atoms";
import { toast } from "react-toastify";
import { Api } from "./Data/Api";
import { processResult } from "./Data/Result";
import { canPerformAction, extractClaims } from "./helpers/AuthorizationManager";
import { ToolButton } from "./Controls/PageBar";
import { useParamIds } from "./Data/Common";
import { InvoiceOrderAdjustModal, OrderAdjustForm } from "./FacilityParkerInvoiceDetailsPageOrderAdjustModal";
export function FacilityParkerInvoiceDetailsPage() {
    const invoice  = useLoaderData()              as Invoice;
    const parker   = useRouteLoaderData("parker") as ParkerDetails;
    const nav = useNavigate();
    const { revalidate } = useRevalidator();
    const { facilityId, parkerId } = useParamIds();
    const { applyOrderAdjust } = useParkerBalance();
    
    const [auth] = useAtom(authAtom);
    const userEmail = auth.account?.emailAddress; 
    const canUserReprintInvoice = canPerformAction(extractClaims(auth), 'can-reprint-invoice');
    const canAdjustOrder        = canPerformAction(extractClaims(auth), 'manage-finance');

    const [showAdjust, setShowAdjust] = useState(false);
    const [adjustOrder, setAdjustOrder] = useState<OrderInfo | null>(null);

    const facility = invoice.facility;
    const orders   = invoice.orders;
    const addMoney = (a: Money, b: Money) => add(a, b);
    const totalOrderBalance = orders.reduce<Money>((sum, order) => addMoney(sum, order.balance.balance), cents(0));
    const isInvoiceUnpaid   = !equal(totalOrderBalance, cents(0));

    async function handleReprintClick() {
        const result = await Api.reprintInvoice(facility.facilityId, parker.parkerId, invoice.invoiceId);
        processResult(result, () => toast.success(`The invoice will be sent to you at ${userEmail} shortly.`), () => toast.error("Failed to reprint invoice. Please try again."));
    }

    async function saveAdjustment(data: OrderAdjustForm, sendInvoice: boolean) {
        if (!adjustOrder) return;
        const req: OrderAdjustReq = {
            orderId:          adjustOrder.orderId,
            currentTotalDebt: adjustOrder.balance.totalDebt,
            newTotalDebt:     cents(data.newTotalDebt),
            reason:           data.reason,
        };
        const res = await Api.orderAdjust(facilityId, parkerId, req);
        if (res.isOk) {
            applyOrderAdjust(subtract(req.newTotalDebt, req.currentTotalDebt));
            if (sendInvoice) {
                const parkerEmail = invoice.parker.emailAddress;
                const reprintRes  = await Api.reprintInvoice(facility.facilityId, parker.parkerId, invoice.invoiceId, parkerEmail, true);
                processResult(
                    reprintRes,
                    () => toast.success(`Updated invoice sent to ${parkerEmail}.`),
                    () => toast.error("Adjustment saved but invoice email failed. Try reprinting manually.")
                );
            } else {
                toast.success("Adjustment saved.");
            }
            revalidate();
        } else {
            toast.error(res.error);
        }
    }

    return <LargePage>
        {adjustOrder !== null && (
            <InvoiceOrderAdjustModal
                order={adjustOrder}
                parkerEmail={invoice.parker.emailAddress}
                show={showAdjust}
                setShow={setShowAdjust}
                onSave={saveAdjustment} />
        )}
        <div className="d-flex align-items-center justify-content-between mb-2">
            <div className="d-flex align-items-center gap-2">
                <div className="d-none d-md-block">
                    <Button variant="link" className="p-0" onClick={() => nav(-1)}>
                        ← Back
                    </Button>
                </div>
                {equal(totalOrderBalance, cents(0)) === true ? (
                    <Badge>Paid</Badge>
                ) : (
                    <Badge>Unpaid</Badge>
                )}
            </div>

            {canUserReprintInvoice && (
                <ToolButton onClick={handleReprintClick}>
                    <Printer  color="maroon" />
                    <span className="d-none d-sm-inline">Reprint</span>
                </ToolButton>
            )}
        </div>

        <div className="p-0 border-0 p-md-3 shadow-md mt-3 rounded-0">
            {canUserReprintInvoice && (
                <div className="small text-muted border rounded p-2 mb-3 bg-light d-flex gap-2">
                    <InfoCircle className="flex-shrink-0 mt-1" />
                    <div>
                        This is an invoice summary. Click <span className="fw-semibold">Reprint</span> to receive the full invoice by email.
                    </div>
                </div>
            )}
            <Row>
                <Col xs={6}>
                    <div className="d-flex flex-column">
                        <strong className="fs-5 text-uppercase">
                            {facility.name}
                        </strong>
                        <div>
                            {showAddressHtml( facility.address )}
                        </div>
                        <div className="mt-1 d-flex-align-center gap-2">
                            <Envelope color="maroon" /> {facility.emailAddress}
                        </div>
                        <div className="d-flex-align-center gap-2">
                            <Telephone color="maroon" /> {facility.phoneNumber}
                        </div>
                    </div>
                </Col>
                <Col xs={6} className="text-md-end">
                    <div className="d-flex justify-content-end align-items-center gap-2">
                    <span>
                        <span className="fw-semibold text-secondary">INVOICE </span>
                        <span className="fw-bold">#{invoice.invoiceNumber}</span>
                    </span>

                    </div>
                    <div>
                        {showInstantDate( invoice.createdWhen, facility.timeZone )}
                    </div>
                </Col>
            </Row>
            <hr />
            <Row className="mb-4 gy-2">
                <Col lg={4}>
                    <div className="fs-4 text-secondary text-uppercase">
                        Account
                    </div>
                    <div className="d-flex flex-column">
                        <div>
                            {parker.name}
                        </div>
                        <div className="d-flex-align-center gap-2">
                            <Envelope color="maroon" />{parker.emailAddress}
                        </div>
                        {!!parker.phoneNumber && <div className="d-flex-align-center gap-2">
                            <Telephone color="maroon" /> {parker.phoneNumber}
                        </div>}
                    </div>
                </Col>
                <Col lg={8}>
                    <div className="fs-4 ms-1 text-secondary text-uppercase">
                        Details
                    </div>
                    <table className="table">
                        <tbody>
                            <tr>
                                <td>Opening Balance</td>
                                <td>{showMoney( invoice.openingBalance )}</td>
                            </tr>
                            {orders.map((order, index) => (
                                <React.Fragment key={order.orderId}>
                                    <tr>
                                        <td colSpan={2} className="bg-light">
                                            <strong>Service {index + 1}</strong>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Service Type</td>
                                        <td>{showOrderType( order.orderType )}</td>
                                    </tr>
                                    <tr>
                                        <td>Service Details</td>
                                        <td>{order.summary}</td>
                                    </tr>
                                    <tr>
                                        <td>Service Amount</td>
                                        <td className="d-flex align-items-center justify-content-between gap-2">
                                            <span>{showMoney( order.balance.totalDebt )}</span>
                                            {canAdjustOrder && isInvoiceUnpaid && (
                                                <ToolButton onClick={() => { setAdjustOrder(order); setShowAdjust(true); }}>
                                                    <PlusSlashMinus className="fs-6" />
                                                    <span className="d-none d-sm-inline">Adjust</span>
                                                </ToolButton>
                                            )}
                                        </td>
                                    </tr>
                                    {toCents(order.balance.totalPaid) > 0 && (
                                        <tr>
                                            <td>Payment</td>
                                            <td>({showMoney(order.balance.totalPaid)})</td>
                                        </tr>
                                    )}
                                    <tr>
                                        <td>Service Balance</td>
                                        <td>{showMoney( order.balance.balance )}</td>
                                    </tr>
                                </React.Fragment>
                            ))}
                            <tr className="table-secondary">
                                <td><strong>Total Service Balance</strong></td>
                                <td><strong>{showMoney( totalOrderBalance )}</strong></td>
                            </tr>                          
                            <tr>
                                <td>Due Date</td>
                                <td>{showDate( invoice.dueWhen )}</td>
                            </tr>
                        </tbody>
                    </table>
                </Col>
            </Row>
            
        </div>
        <div className="d-flex flex-column justify-content-center gap-2 ms-2 mt-3 text-muted">
            {invoice.emailSentWhen !== null ? <div>
                Email Sent {showInstantDateTime( invoice.emailSentWhen, facility.timeZone )}
            </div> : <></>}
        </div>
        <Button type="button" className="w-50 mt-3 d-md-none" onClick={() => nav(-1)}>
            Back
        </Button>
    </LargePage>
}
